﻿import {hasFlag, isNullOrEmpty, StringNamedValue, translation} from "@2d-front-monorepo/core";
import {Icon, IMvvmContextState, MvvmSelect, MvvmTextInput, useMvvmFieldSubscription} from "@2d-front-monorepo/design-library";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX} from "react";
import VehiclePropertyFlags from "../enums/VehiclePropertyFlags";
import {useFieldValidation} from "../helpers/useFieldValidation";
import ICreateComponentAttachments from "../interfaces/ICreateComponentAttachments";
import {CreateVehicleArguments} from "../types/arguments/CreateVehicleArguments";
import {EntityProfile} from "../types/InstallationByHostData";
import {ComponentWithChildrenParts} from "./ComponentParts";
import FullCreateAttachmentsComponent from "./FullCreateAttachmentsComponent";

export interface CreateVehicleComponentProps extends ComponentWithChildrenParts<CreateVehicleModel> {
	colors: StringNamedValue[];
	configuration: EntityProfile<VehiclePropertyFlags>;
	context: IMvvmContextState<CreateVehicleModel>;
	models: StringNamedValue[];
	plateValidation: PlateValidationRequirements;
	provinces: StringNamedValue[];
}

export interface PlateValidationRequirements {
	existing: string[];
	readOnly?: boolean;

	/** When set, a duplicated plate offers a button that picks the existing vehicle instead of
	 *  leaving the user to find it in the select on their own. */
	onSelectExisting?: (plate: string) => void;
}

/** Marks the duplicated-plate error so the "use the existing one" button can show up with that
 *  message and only with it - see ErrorEntry.type. */
export const DUPLICATED_PLATE = "duplicated-plate";

/** The duplicate rule, shared by the plate validator and the "use the existing one" button so the
 *  two can never disagree about what counts as a duplicate. */
export function findDuplicatePlate(existing: string[], plate: string | undefined): string | undefined {
	if (isNullOrEmpty(plate)) return undefined;

	const upperPlate = plate.toUpperCase();
	return existing.find(x => x.toUpperCase() === upperPlate);
}

export interface CreateVehicleModel extends ICreateComponentAttachments {
	arguments: CreateVehicleArguments;
	customData: Record<string, any>;
}

export default function CreateVehicleComponent(props: CreateVehicleComponentProps): JSX.Element {
	const {
		context,
		configuration,
		plateValidation,
		colors,
		models,
		provinces,
		header,
		children,
		footer
	} = props;

	const readOnlyFields = configuration.fieldRules.readOnly || VehiclePropertyFlags.None;
	const hiddenFields = configuration.fieldRules.hidden || VehiclePropertyFlags.None;

	const nickNameValidation = useFieldValidation<VehiclePropertyFlags, string>(configuration.fieldRules, VehiclePropertyFlags.Nickname, "vehicle", "nickname");
	const colorValidation = useFieldValidation<VehiclePropertyFlags, string>(configuration.fieldRules, VehiclePropertyFlags.Color, "vehicle", "color");
	const modelValidation = useFieldValidation<VehiclePropertyFlags, string>(configuration.fieldRules, VehiclePropertyFlags.Model, "vehicle", "model");
	const provinceValidation = useFieldValidation<VehiclePropertyFlags, string>(configuration.fieldRules, VehiclePropertyFlags.Province, "vehicle", "province");

	const plateValidationObject = useFieldValidation<VehiclePropertyFlags, string>(configuration.fieldRules, VehiclePropertyFlags.Plate, "vehicle", "plate", plate => {
		const duplicated = findDuplicatePlate(plateValidation.existing, plate);
		if (duplicated == null) return undefined;

		const args = {value: duplicated.toUpperCase()};
		if (plateValidation.onSelectExisting == null) {
			return translation(PermitTranslations.validation__vehicle__plate__duplicated, args);
		}

		// There IS a button to press, so point at it - falling back to the plain message on the
		// installations that have not imported the new key yet.
		return {
			type: DUPLICATED_PLATE,
			error: translation([
				{text: PermitTranslations.validation__vehicle__plate__duplicated__select, args},
				{text: PermitTranslations.validation__vehicle__plate__duplicated, args}
			])
		};
	});

	// Ride the error, not the raw value: validation runs on blur while the model is written on every
	// keystroke, so keying off the value alone showed the button before its explanation. Only the
	// error channel is subscribed for the same reason - the value one would re-render per keystroke.
	useMvvmFieldSubscription(context, "arguments.plate", {subscribeValue: false});
	const plateError = context.getError("arguments.plate");
	const duplicatedPlate = plateError?.type !== DUPLICATED_PLATE
		? undefined
		: findDuplicatePlate(plateValidation.existing, plateError.value);

	const typeValidation = useFieldValidation<VehiclePropertyFlags, string>(configuration.fieldRules, VehiclePropertyFlags.Type, "vehicle", "type", type => {
		if (isNullOrEmpty(type)) return undefined;
		const year = parseInt(type, 10);
		return isNaN(year) || year < 1900 || year > (new Date().getFullYear() + 1)
			? translation(PermitTranslations.validation__vehicle__type__invalid)
			: undefined;
	});

	return (<div className="layout vertical gap">
		{header?.(context.state)}
		<div className="grid">
			<MvvmTextInput context={context}
			               property="arguments.plate"
			               type="text"
			               column="col-1of4"
			               autoFocus
			               disabled={hasFlag(readOnlyFields, VehiclePropertyFlags.Plate) || plateValidation.readOnly}
			               hidden={hasFlag(hiddenFields, VehiclePropertyFlags.Plate)}
			               validation={plateValidationObject.validation}
			               transform={plateValidationObject.transform}
			               minLength={plateValidationObject.minLength}
			               maxLength={plateValidationObject.maxLength}
			               controlSuffix={duplicatedPlate == null ? undefined :
				               <button type="button" onClick={() => plateValidation.onSelectExisting!(duplicatedPlate)}>
					               <Icon icon="use-existing" title={translation(PermitTranslations.new_vehicle__choose)}/>
				               </button>}
			               label={translation(PermitTranslations.new_vehicle__plate)}/>

			<MvvmTextInput context={context}
			               property="arguments.data.nickname"
			               type="text"
			               column="col-1of4"
			               disabled={hasFlag(readOnlyFields, VehiclePropertyFlags.Nickname)}
			               hidden={hasFlag(hiddenFields, VehiclePropertyFlags.Nickname)}
			               validation={nickNameValidation.validation}
			               transform={nickNameValidation.transform}
			               minLength={nickNameValidation.minLength}
			               maxLength={nickNameValidation.maxLength}
			               label={translation(PermitTranslations.new_vehicle__nickname)}/>

			<MvvmSelect context={context}
			            property="arguments.data.color"
			            items={colors}
			            placeholder={translation(PermitTranslations.vehicle__color__placeholder)}
			            column="col-1of4"
			            disabled={hasFlag(readOnlyFields, VehiclePropertyFlags.Color)}
			            hidden={hasFlag(hiddenFields, VehiclePropertyFlags.Color)}
			            validation={colorValidation.validation}
			            label={translation(PermitTranslations.new_vehicle__color)}/>

			<MvvmSelect context={context}
			            property="arguments.data.model"
			            items={models}
			            placeholder={translation(PermitTranslations.vehicle__model__placeholder)}
			            column="col-1of4"
			            disabled={hasFlag(readOnlyFields, VehiclePropertyFlags.Model)}
			            hidden={hasFlag(hiddenFields, VehiclePropertyFlags.Model)}
			            validation={modelValidation.validation}
			            label={translation(PermitTranslations.new_vehicle__model)}/>

			<MvvmTextInput context={context}
			               property="arguments.data.type"
			               type="text"
			               inputMode="numeric"
			               column="col-1of4"
			               placeholder="YYYY"
			               disabled={hasFlag(readOnlyFields, VehiclePropertyFlags.Type)}
			               hidden={hasFlag(hiddenFields, VehiclePropertyFlags.Type)}
			               validation={typeValidation.validation}
			               transform={typeValidation.transform}
			               minLength={typeValidation.minLength}
			               maxLength={typeValidation.maxLength}
			               label={translation(PermitTranslations.new_vehicle__type)}/>

			<MvvmSelect context={context}
			            property="arguments.data.province"
			            items={provinces}
			            placeholder={translation(PermitTranslations.vehicle__province__placeholder)}
			            column="col-1of4"
			            disabled={hasFlag(readOnlyFields, VehiclePropertyFlags.Province)}
			            hidden={hasFlag(hiddenFields, VehiclePropertyFlags.Province)}
			            validation={provinceValidation.validation}
			            label={translation(PermitTranslations.new_vehicle__province)}/>
		</div>
		{children}
		<FullCreateAttachmentsComponent context={context} attachments={configuration.attachments}/>
		{footer?.(context.state)}
	</div>);
}
